/* BeginnersFlow: the body of the Claude for Beginners page (Esther, 2026-10-30).

   Replaces the template sections between the hero and the tickets with a flow
   modelled on how claude.com explains its product: a numbered promise row, a
   tabbed module explorer where each tab plays a short "you ask, Claude works,
   you get the result" demo against a time rail of the real agenda, a numbered
   "you'll leave with" grid, and a map of everything covered. All drawn in our
   own Vivid oranges and type, square corners, no Anthropic assets or marks.

   Styles live in cowork-ds.css under `.bf-`. Only this series renders it. */

import { useEffect, useRef, useState } from "react";
import { type ClaudeFlowScript } from "./claude-flow-demo";
import { requestCheckout } from "@/lib/open-checkout";
import { PersonaCarousel, type Persona } from "@/components/sections/persona-carousel";
import { DsIcon } from "@/components/sections/ds-icons";

/* ── reveal on scroll ─────────────────────────────────────────────────────── */
function useInView<T extends HTMLElement>(threshold = 0.2) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView] as const;
}

/* The painting behind a band drifts against the scroll, so the page feels
   like it moves through it. Sets --bf-par (px) on the band; CSS moves the art. */
function useParallax<T extends HTMLElement>(ref: React.RefObject<T | null>, strength = 0.18) {
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        const mid = r.top + r.height / 2 - window.innerHeight / 2;
        el.style.setProperty("--bf-par", `${(-mid * strength).toFixed(1)}px`);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, [ref, strength]);
}

function ReserveButton({ label = "Reserve your seat" }: { label?: string }) {
  // The approved site CTA: the hero's own classes, so the drawn outline and
  // its colours come from the design system, not from this page.
  return (
    <a
      href="#tickets"
      className="btn btn-cta btn-lg ch-btn bf-cta"
      onClick={(e) => {
        if (requestCheckout()) e.preventDefault();
      }}
    >
      {label}
    </a>
  );
}

/* ── 1. The promise + the reel ───────────────────────────────────────────── */
/* Highlight trailer cut from Isaac's Oct 13 + Oct 23 Cowork classes
   (.gen-work/beginners/reel/v1). Plays muted on loop; controls let a visitor
   turn on Isaac's voice. */
const REEL = {
  src: "/videos/beginners/trailer.mp4",
  poster: "/videos/beginners/trailer-poster.webp",
  label: "Inside the workshop: live, hands-on, step-by-step instruction on attendees' own workflows",
};

function PromiseRow() {
  const [ref, inView] = useInView<HTMLElement>();
  const video = useRef<HTMLVideoElement>(null);
  useParallax(ref);
  /* Page speed: the trailer downloads nothing until the section scrolls into
     view, then starts muted. The 1080p master is in .gen-work/beginners/reel/
     page-originals; the page serves a 720p cut (~5.6 MB). */
  useEffect(() => {
    const v = video.current;
    if (!inView || !v) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    v.play().catch(() => {});
  }, [inView]);
  return (
    <section ref={ref} className="bf-section bf-dark bf-promise" data-in={inView} aria-labelledby="bf-promise-h">
      <div className="bf-dark-art bf-dark-art-wheat" aria-hidden="true" />
      <h2 id="bf-promise-h" className="bf-h2 bf-center">
        Claude can do a lot. That's what makes it hard to start.
      </h2>
      <figure className="bf-reel">
        <video
          ref={video}
          src={REEL.src}
          poster={REEL.poster}
          width={1280}
          height={720}
          muted
          loop
          playsInline
          controls
          preload="none"
          aria-label={REEL.label}
        />
      </figure>
    </section>
  );
}

/* ── 2. The module explorer ───────────────────────────────────────────────── */
interface Module {
  short: string;
  title: string;
  time: string;
  blurb: string;
  body: string;
  inClass: string;
  demo: ClaudeFlowScript;
}

const MODULES: Module[] = [
  {
    short: "Set up Claude",
    title: "Set Claude up to know you",
    time: "10:00 AM",
    blurb: "Your files, your brand, your voice",
    body: "You write a short file about your job, your clients, and how you like things done. Claude reads it before every answer, so you only explain yourself once.",
    inClass: "You write your own CLAUDE.md, brand file, and voice note.",
    demo: {
      label: "Demo: inside a Project with your CLAUDE.md, brand file and voice note loaded, you ask for a LinkedIn post. Claude reads all three and returns a post in your voice.",
      context: { label: "Project: My work", files: ["CLAUDE.md", "brand.md", "my-voice.md"], fly: true },
      prompt: "Write the LinkedIn post announcing our spring launch.",
      steps: ["Reading CLAUDE.md: who you are and what you do", "Following brand.md: the words you use and avoid", "Matching my-voice.md: how you actually sound"],
      result: {
        title: "LinkedIn post · Spring launch",
        lines: ["We've spent six months on this one.", "Starting April 8, every order ships in 2 days,", "and returns are free, no questions asked.", "Thank you for waiting. It was worth it."],
      },
    },
  },
  {
    short: "Better prompts",
    title: "Write prompts that get good answers",
    time: "10:30 AM",
    blurb: "Context, task, example, format",
    body: "You learn the four parts of a good prompt: the context, the task, an example, and the format you want. Then you try it on your own work.",
    inClass: "You rewrite one of your own tasks live and compare the results.",
    demo: {
      label: "Demo: you share a proposal you liked and ask Claude to question you, then draft a new one. Claude reads the example, asks three questions, and returns a draft in the same structure.",
      context: { label: "New chat", files: ["proposal-we-won.docx"] },
      prompt: "Here's a proposal I liked. Ask me 3 questions, then draft one for Acme.",
      steps: ["Reading the example you liked", "Asking you three quick questions", "Drafting in the same structure"],
      result: {
        title: "Proposal draft · Acme",
        lines: ["Goal: cut new-hire onboarding time in half.", "Scope: three workshops and a 30-day check-in.", "Timeline: kickoff Jan 12, wrap Feb 20.", "Next step: a yes on the January start."],
      },
    },
  },
  {
    short: "Gmail & Calendar",
    title: "Connect Gmail, Calendar, and Drive",
    time: "11:00 AM",
    blurb: "Gmail, Drive, Calendar",
    body: "You connect your accounts so Claude can read your email, calendar, and files. Then you ask it what you need to know today. You choose what it can see.",
    inClass: "We connect your tools live and walk through the privacy settings.",
    demo: {
      label: "Demo: with Gmail, Google Drive and Calendar connected, you ask Claude to prep you for tomorrow's meeting. Claude finds the meeting, reads the email thread and the proposal, and returns a one-page brief.",
      context: { label: "Connected", files: ["Gmail", "Google Drive", "Calendar"] },
      apps: [
        { name: "Gmail", src: "/images/cowork-catalog/connectors/gmail.svg" },
        { name: "Drive", src: "/images/cowork-catalog/connectors/google-drive.svg" },
        { name: "Calendar", src: "/images/cowork-catalog/connectors/google-calendar.svg" },
        { name: "Slack", src: "/images/cowork-catalog/connectors/slack.svg" },
        { name: "Notion", src: "/images/cowork-catalog/connectors/notion.svg" },
      ],
      stepIcons: [
        "/images/cowork-catalog/connectors/google-calendar.svg",
        "/images/cowork-catalog/connectors/gmail.svg",
        "/images/cowork-catalog/connectors/google-drive.svg",
      ],
      prompt: "Prep me for tomorrow's 10 AM with the Acme team.",
      steps: ["Finding the meeting on your calendar", "Reading the latest Acme email thread", "Pulling the proposal from Drive"],
      result: {
        title: "Meeting prep · Acme, 10 AM",
        lines: ["Who's coming: Dana (VP Ops) and Raj (IT).", "Where it stands: they liked the January start.", "Open question: pricing for 40 seats.", "Your ask: a signed scope by Friday."],
      },
    },
  },
  {
    short: "Finished docs",
    title: "Turn notes into a finished document",
    time: "11:30 AM",
    blurb: "Documents, slides, charts",
    body: "You give Claude your notes and ask for a real file: a one-page summary, a slide outline, or a spreadsheet. You get a file you can edit and send.",
    inClass: "You make a document you'd actually send, from your own notes.",
    demo: {
      label: "Demo: you hand Claude messy meeting notes and ask for a one-page summary for your boss. Claude sorts the notes and returns a formatted one-pager.",
      context: { label: "New chat", files: ["meeting-notes.txt"] },
      prompt: "Turn these notes into a one-page summary for my boss.",
      steps: ["Sorting notes into decisions and to-dos", "Writing the one-page summary", "Formatting it to send"],
      result: {
        title: "Q4 planning · One-page summary",
        lines: ["Decided: the spring launch moves to April 8.", "Needs a call: hiring a second designer.", "Owners: Maya on the brief, Jon on budget.", "Next check-in: Thursday at 2 PM."],
      },
    },
  },
  {
    short: "Automations",
    title: "Automate a weekly task",
    time: "12:00 PM",
    blurb: "Skills and schedules",
    body: "You pick a task you do every week, like a Monday report. Claude saves the steps and runs them on the schedule you set.",
    inClass: "You turn one of your weekly tasks into a skill and schedule it.",
    demo: {
      label: "Demo: you ask Claude to send you last week's numbers every Monday in your format. Claude saves it as a skill, schedules it, and runs it once to show the result.",
      context: { label: "Skill: Weekly numbers", files: ["my-format.pdf"] },
      prompt: "Every Monday at 8, send me last week's numbers in this format.",
      steps: ["Saving your format as a skill", "Scheduling it for Mondays at 8 AM", "Running it once now to check"],
      result: {
        title: "Weekly numbers · Monday, 8:00 AM",
        lines: ["Leads: 214, up 9% on last week.", "Sign-ups: 38.", "Best source: the webinar email.", "Worth a look: paid social cost per lead rose."],
      },
    },
  },
];

/* Master Claude in 3 hours as a tabbed agenda with Claude at work (Esther,
   2026-11-02): five parts across the top, each with a loading bar. Under them a
   Claude window plays that part for real: the prompt is typed, Claude's steps
   run (reading files, checking Gmail, saving a skill), and the finished thing
   lands. The bar follows the scene; when it fills, the next part starts. Click
   any part to jump. Plays only while on screen; reduced motion shows each
   scene's end state, with no autoplay. */
const ICON = (n: string) => `/images/cowork-catalog/connectors/${n}.svg`;
const CLAUDE_MARK = "/images/brand/anthropic-asterisk-256.webp";

type Beat =
  | { k: "ctx"; label: string; files: string[] }
  | { k: "user"; text: string; file?: string; say?: string }
  | { k: "step"; text: string; icon?: string; say?: string }
  | { k: "reply"; view: (since: number) => React.ReactNode; hold?: number; say?: string }
  | { k: "strike" }
  /* A prompt that builds up one labeled part at a time. */
  | { k: "prompt"; parts: { label: string; text: string; file?: string; say: string }[] };

const TYPE_CPS = 42;
const SCENE_HOLD = 4500;
const PART_MS = 1900;

function Row({ since, at, children }: { since: number; at: number; children: React.ReactNode }) {
  return since >= at ? <div className="ac-in">{children}</div> : null;
}

const SCENES: Beat[][] = [
  [
    { k: "ctx", label: "Project · My work", files: ["CLAUDE.md", "Brand voice", "How I write"] },
    { k: "user", text: "Write an email to Dana at Acme about Friday's review.", say: "You ask Claude to write an email." },
    { k: "step", text: "Read CLAUDE.md", say: "Claude reads your file about you first." },
    { k: "step", text: "Matched how you write" },
    {
      k: "reply",
      say: "The email sounds like you wrote it.",
      view: () => (
        <p>Hi Dana, quick one: Friday's review moves to 2 PM. The three numbers you asked for are below. Nothing else needed from you.</p>
      ),
    },
  ],
  [
    { k: "user", text: "Write the opening of our Acme proposal.", say: "A short prompt with nothing to go on…" },
    { k: "reply", hold: 1500, say: "…gets a generic answer.", view: () => <p>We are pleased to present this proposal for your consideration and look forward to a mutually beneficial partnership.</p> },
    { k: "strike" },
    {
      k: "prompt",
      parts: [
        { label: "Context", text: "Acme is a 400-person logistics company. New hires take six weeks to get up to speed.", say: "1. Context: who it's for and what's going on." },
        { label: "Task", text: "Write the opening paragraph of our proposal to fix their onboarding.", say: "2. Task: exactly what you want." },
        { label: "Example", text: "Match the tone of this proposal, which won.", file: "winning-proposal.pdf", say: "3. Example: one that worked before." },
        { label: "Format", text: "Two sentences. Plain words. Lead with the result.", say: "4. Format: how the answer should look." },
      ],
    },
    { k: "reply", say: "The same ask, now specific and ready to send.", view: () => <p><b>Acme, we can get your new hires up to speed in three weeks instead of six.</b> Here's the plan, starting January 12.</p> },
  ],
  [
    { k: "user", text: "What do I need to know this morning?", say: "You ask what's on today." },
    { k: "step", text: "Searched Gmail", icon: "gmail", say: "Claude checks Gmail, Calendar, and Drive." },
    { k: "step", text: "Checked Google Calendar", icon: "google-calendar" },
    { k: "step", text: "Read Google Drive", icon: "google-drive" },
    {
      k: "reply",
      say: "You get a short list of what matters.",
      view: (since) => (
        <ul className="ac-list">
          <Row since={since} at={0}><li><img src={ICON("gmail")} alt="" /><span><b>Dana at Acme</b> wants the revised quote. Your reply is drafted.</span></li></Row>
          <Row since={since} at={450}><li><img src={ICON("google-calendar")} alt="" /><span><b>2:00 PM, Acme kickoff.</b> They care most about the launch date.</span></li></Row>
          <Row since={since} at={900}><li><img src={ICON("google-drive")} alt="" /><span><b>Q4 plan</b> updated with yesterday's numbers.</span></li></Row>
        </ul>
      ),
    },
  ],
  [
    { k: "user", text: "Turn my notes into a one-page Q4 summary.", file: "q4-meeting-notes.docx", say: "You upload your meeting notes." },
    { k: "step", text: "Read q4-meeting-notes.docx", say: "Claude writes a one-page summary." },
    { k: "step", text: "Created Q4 summary.docx" },
    {
      k: "reply",
      say: "You get a Word file to edit and send.",
      view: (since) => (
        <div className="ac-doc">
          <p className="ac-doc-name"><span className="ac-doc-icon" />Q4 summary.docx</p>
          <div className="ac-doc-body">
            <p className="ac-doc-title">Q4 planning · One-page summary</p>
            <Row since={since} at={300}><p>Decided: the spring launch moves to April 8.</p></Row>
            <Row since={since} at={700}><p>Needs a call: hiring a second designer.</p></Row>
            <Row since={since} at={1100}><p>Owners: Maya on the brief, Jon on budget.</p></Row>
            <Row since={since} at={1500}><p>Next check-in: Thursday at 2 PM.</p></Row>
          </div>
        </div>
      ),
    },
  ],
  [
    { k: "user", text: "Every Monday at 8, send me last week's numbers.", say: "You describe a weekly task." },
    { k: "step", text: "Saved as a skill: Monday numbers", say: "Claude saves it as a skill." },
    {
      k: "reply",
      hold: 1700,
      say: "You turn it on.",
      view: (since) => (
        <div className="ac-skill">
          <div>
            <p className="ac-skill-name">Monday numbers</p>
            <p className="ac-skill-when">Every Monday · 8:00 AM</p>
          </div>
          <span className="ac-switch" data-on={since > 700 ? "true" : "false"}><i /></span>
        </div>
      ),
    },
    {
      k: "reply",
      say: "It runs every Monday at 8.",
      view: () => (
        <div className="ac-note">
          <span className="ac-note-time">Monday, 8:00 AM</span>
          <p><b>Your numbers are ready.</b> Leads 214, up 9% on last week. Best source: the webinar email.</p>
        </div>
      ),
    },
  ],
];

/* When each beat starts and ends, in ms from the scene's start. */
function timeline(beats: Beat[]) {
  let t = 350;
  const times = beats.map((b) => {
    if (b.k === "ctx") return { at: 0, end: 0 };
    const at = t;
    if (b.k === "user") t += (b.text.length / TYPE_CPS) * 1000 + 450;
    else if (b.k === "prompt") t += b.parts.length * PART_MS + 300;
    else if (b.k === "step") t += 750;
    else if (b.k === "reply") t += b.hold ?? 900;
    else t += 800;
    return { at, end: t };
  });
  return { times, total: t + SCENE_HOLD };
}
const TIMELINES = SCENES.map(timeline);

function ClaudeScene({ index, t }: { index: number; t: number }) {
  const beats = SCENES[index];
  const { times } = TIMELINES[index];
  const body = useRef<HTMLDivElement>(null);
  let typing = "";
  const shown: React.ReactNode[] = [];
  let ctx: Extract<Beat, { k: "ctx" }> | null = null;

  let turnOpen = false;
  const mark = () => {
    if (turnOpen) return null;
    turnOpen = true;
    return <img className="ac-mark" src={CLAUDE_MARK} alt="" />;
  };
  beats.forEach((b, i) => {
    const { at, end } = times[i];
    if (b.k === "ctx") { ctx = b; return; }
    if (b.k === "user" || b.k === "prompt") turnOpen = false;
    if (t < at) return;
    if (b.k === "user") {
      if (t < end - 250) { typing = b.text.slice(0, Math.floor(((t - at) / 1000) * TYPE_CPS)); return; }
      shown.push(
        <div key={i} className="ac-user ac-in">
          {b.file && <span className="ac-file"><span className="ac-doc-icon" />{b.file}</span>}
          <p>{b.text}</p>
        </div>,
      );
    } else if (b.k === "prompt") {
      shown.push(
        <div key={i} className="ac-user ac-prompt ac-in">
          {b.parts.map((pt, j) =>
            t >= at + j * PART_MS ? (
              <div key={pt.label} className="ac-part ac-in">
                <span className="ac-part-label">{pt.label}</span>
                {pt.file && <span className="ac-file"><span className="ac-doc-icon" />{pt.file}</span>}
                <p>{pt.text}</p>
              </div>
            ) : null,
          )}
        </div>,
      );
    } else if (b.k === "step") {
      shown.push(
        <p key={i} className="ac-step ac-in" data-done={t >= end ? "true" : "false"}>
          {mark()}
          {b.icon ? <img src={ICON(b.icon)} alt="" /> : <span className="ac-step-dot" />}
          {b.text}
        </p>,
      );
    } else if (b.k === "reply") {
      const struck = beats[i + 1]?.k === "strike" && t >= times[i + 1].at + 250;
      shown.push(
        <div key={i} className="ac-reply ac-in" data-struck={struck ? "true" : "false"}>
          {mark()}
          {b.view(t - at)}
        </div>,
      );
    }
  });

  /* The newest message stays in view, like the real app. */
  useEffect(() => {
    const el = body.current;
    if (el) el.scrollTop = el.scrollHeight;
  });

  const c = ctx as Extract<Beat, { k: "ctx" }> | null;
  let say = "";
  beats.forEach((b, i) => {
    if (b.k === "prompt") b.parts.forEach((pt, j) => { if (t >= times[i].at + j * PART_MS) say = pt.say; });
    else if (b.k !== "ctx" && b.k !== "strike" && b.say && t >= times[i].at) say = b.say;
  });
  return (
    <>
    <div className="rc-stage" aria-hidden="true">
      <div className="ac-win">
        <div className="ac-top">
          <img src={CLAUDE_MARK} alt="" />
          <span>{c ? c.label : "New chat"}</span>
        </div>
        {c && (
          <div className="ac-ctx">
            {c.files.map((f) => <span key={f} className="ac-file"><span className="ac-doc-icon" />{f}</span>)}
          </div>
        )}
        <div className="ac-body" ref={body}>
          {shown}
        </div>
        <div className="ac-input" data-typing={typing ? "true" : "false"}>
          <span className="ac-input-text">{typing || "Reply to Claude…"}{typing && <i className="ac-caret" />}</span>
          <span className="ac-send" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="14" height="14"><path d="M12 19V5M5 12l7-7 7 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </span>
        </div>
      </div>
    </div>
    {say && (
      <p className="ac-say" key={say}>
        <span aria-hidden="true" />
        {say}
      </p>
    )}
    </>
  );
}

function Agenda() {
  const [active, setActive] = useState(0);
  const [t, setT] = useState(0);
  const [onScreen, setOnScreen] = useState(false);
  const [reduced, setReduced] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const swipeX = useRef(0);
  const m = MODULES[active];
  const total = TIMELINES[active].total;

  useEffect(() => {
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  /* One clock drives both the scene and the bar, and stops while off screen. */
  const playing = onScreen && !reduced;
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const id = window.setInterval(() => {
      const now = performance.now();
      const step = now - last;
      last = now;
      setT((x) => x + step);
    }, 50);
    return () => window.clearInterval(id);
  }, [playing]);

  useEffect(() => {
    if (t >= total) {
      setActive((a) => (a + 1) % MODULES.length);
      setT(0);
    }
  }, [t, total]);

  /* Keep the active tab visible in the swipeable row on phones, without
     moving the page. */
  useEffect(() => {
    const tab = tabs.current[active];
    const row = tab?.parentElement;
    if (tab && row && row.scrollWidth > row.clientWidth) {
      const left = row.scrollLeft + tab.getBoundingClientRect().left - row.getBoundingClientRect().left - 16;
      row.scrollTo({ left, behavior: "smooth" });
    }
  }, [active]);

  const pick = (i: number) => {
    setActive(i);
    setT(0);
  };
  const sceneT = reduced ? Number.MAX_SAFE_INTEGER : t;

  return (
    <section ref={ref} id="curriculum" className="bf-section bf-explorer bf-explorer-monet bf-agenda" aria-labelledby="bf-learn-h">
      <h2 id="bf-learn-h" className="bf-h2 bf-center">
        Master Claude in 3 hours
      </h2>
      <div className="bf-ag-tabs" role="tablist" aria-label="The five parts of the morning">
        {MODULES.map((mod, i) => (
          <button
            key={mod.title}
            ref={(el) => { tabs.current[i] = el; }}
            type="button"
            role="tab"
            id={`bf-ag-tab-${i}`}
            aria-selected={i === active}
            aria-controls="bf-ag-panel"
            className="bf-ag-tab"
            data-state={i === active ? "now" : i < active ? "past" : "next"}
            onClick={() => pick(i)}
          >
            <span className="bf-ag-bar" aria-hidden="true">
              {i === active && <i style={{ transform: `scaleX(${reduced ? 1 : Math.min(1, t / total)})` }} />}
            </span>
            <span className="bf-ag-time">{mod.time}</span>
            <span className="bf-ag-title">{mod.short}</span>
          </button>
        ))}
      </div>
      <div className="bf-ag-panel" role="tabpanel" id="bf-ag-panel" aria-labelledby={`bf-ag-tab-${active}`}>
        {/* Phones only (CSS): which part this is, with back and next. */}
        <div className="bf-ag-step">
          <button type="button" aria-label="Previous part" onClick={() => pick((active + MODULES.length - 1) % MODULES.length)}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <span>{m.time}</span>
          <button type="button" aria-label="Next part" onClick={() => pick((active + 1) % MODULES.length)}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
        <div className="bf-ag-copy" key={`copy-${active}`}>
          <p className="bf-ag-kicker">{m.time}</p>
          <h3 className="bf-ag-head">{m.title}</h3>
          <p className="bf-body">{m.body}</p>
        </div>
        <div
          className="bf-ag-visual"
          onTouchStart={(e) => { swipeX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            const dx = e.changedTouches[0].clientX - swipeX.current;
            if (Math.abs(dx) > 50) pick((active + (dx < 0 ? 1 : MODULES.length - 1)) % MODULES.length);
          }}
        >
          <ClaudeScene key={active} index={active} t={sceneT} />
        </div>
      </div>
    </section>
  );
}

/* ── 3. What you leave with: an uneven bento of Monet and Vivid tiles ────────
   Each tile pairs its ground with its own type colour (Vivid stage rule):
   Sunset and Tangerine carry Ink, Rust carries Paper, the Monet tiles sit under
   a dark wash with Paper type. */
const APP = (n: string) => `/images/cowork-catalog/connectors/${n}.svg`;
/* The apps that float into the tools tile: [logo, x %, y %] around the centre. */
const TOOL_LOGOS: [string, number, number][] = [
  ["gmail", 18, 16], ["google-drive", 44, 8], ["google-calendar", 72, 14], ["slack", 88, 36],
  ["notion", 10, 42], ["google-docs", 82, 62], ["google-sheets", 24, 70], ["figma", 58, 78],
  ["hubspot", 36, 88], ["canva", 90, 84], ["outlook", 6, 76], ["zoom", 66, 32],
  ["salesforce", 30, 30], ["asana", 50, 96],
];

/* The schedule tile's chrome switch (Esther, 2026-11-02): off until the tile is
   well into view, flips on as you reach it, and back off when you scroll away,
   so it toggles every pass. Reduced motion: shown on, still. */
function SkillSwitch() {
  const ref = useRef<HTMLSpanElement>(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setOn(true);
      return;
    }
    const io = new IntersectionObserver(([e]) => setOn(e.isIntersecting), { rootMargin: "0px 0px -30% 0px", threshold: 1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <span ref={ref} className="bf-chrome-switch" data-on={on ? "true" : "false"}>
      <span className="bf-chrome-knob" />
    </span>
  );
}

/* Fires once when an element is well into view, not when its section first
   peeks in, so the visitor sees the thing arrive. Reduced motion: at once. */
function useArrive<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [arrived, setArrived] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setArrived(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setArrived(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -25% 0px", threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, arrived] as const;
}

/* Tools tile (Esther, 2026-11-02): a blurred Monet that starts empty; the
   Claude mark and the app logos float in once the tile is well into view. */
function ToolCloud() {
  const [ref, arrived] = useArrive<HTMLDivElement>();
  return (
    <div ref={ref} className="bf-tile-visual bf-cloud" data-arrived={arrived ? "true" : "false"} aria-hidden="true">
      {/* Esther, 2026-10-30: the Claude mark is OK here, since it's what we teach. */}
      <span className="bf-cloud-core">
        <img src="/images/brand/anthropic-asterisk-256.webp" alt="" />
        <span>Claude</span>
      </span>
      {TOOL_LOGOS.map(([n, x, y], i) => (
        <span key={n} className="bf-cloud-app" style={{ ["--x" as string]: `${x}%`, ["--y" as string]: `${y}%`, ["--i" as string]: i }}>
          <img src={APP(n)} alt="" />
        </span>
      ))}
    </div>
  );
}

function LeaveWith() {
  const [ref, inView] = useInView<HTMLElement>(0.12);
  return (
    <section ref={ref} className="bf-section bf-leave" data-in={inView} aria-labelledby="bf-leave-h">
      <h2 id="bf-leave-h" className="bf-h2 bf-center">
        By 1 PM, you'll have
      </h2>
      <ul className="bf-bento">
        <li className="bf-tile bf-tile-account bf-tile-monet bf-tile-wheat">
          <div className="bf-tile-visual" aria-hidden="true">
            <span className="bf-chip bf-chip-ok">CLAUDE.md</span>
            <span className="bf-chip bf-chip-ok">brand.md</span>
            <span className="bf-chip bf-chip-ok">my-voice.md</span>
          </div>
          <h3 className="bf-tile-title">Claude that knows you</h3>
          <p className="bf-tile-body">Your work, your brand and your voice written down once, so every answer starts already briefed.</p>
        </li>

        <li className="bf-tile bf-tile-tools bf-tile-monet bf-tile-grain">
          <ToolCloud />
          <h3 className="bf-tile-title">Your tools connected</h3>
          <p className="bf-tile-body">Claude working inside your email, docs, calendar, and the apps you already use, with the access you chose.</p>
        </li>

        <li className="bf-tile bf-tile-work bf-tile-monet bf-tile-irises">
          {/* A finished, branded one-pager for a made-up client, in its own brand
              (Navy and Cloud), the way a real deliverable would look. */}
          <div className="bf-tile-visual bf-brand-doc" aria-hidden="true">
            <span className="bf-brand-head">
              <span className="bf-brand-mark" />
              <span className="bf-brand-name">Harbor &amp; Co.</span>
              <span className="bf-brand-meta">Q4 plan</span>
            </span>
            <span className="bf-brand-body">
              <span className="bf-brand-kicker">One-page summary</span>
              <span className="bf-brand-title">Spring launch moves to April 8</span>
              <span className="bf-brand-chart">
                <span style={{ height: "38%" }} /><span style={{ height: "55%" }} /><span style={{ height: "47%" }} /><span style={{ height: "72%" }} /><span style={{ height: "90%" }} />
              </span>
              <span className="bf-brand-row"><b>Needs a call</b> A second designer</span>
              <span className="bf-brand-row"><b>Next check-in</b> Thursday, 2 PM</span>
            </span>
          </div>
          <h3 className="bf-tile-title">One finished piece of work</h3>
          <p className="bf-tile-body">A document made from your own notes that you'd actually send.</p>
        </li>

        <li className="bf-tile bf-tile-prompts bf-tile-rust">
          {/* One short prompt, its four parts highlighted inline (Esther, 2026-11-02),
              with a one-line key underneath. */}
          <div className="bf-tile-visual bf-prompt-anatomy" aria-hidden="true">
            <p className="bf-pa-prompt">
              <mark data-k="context">I run a design studio.</mark>{" "}
              <mark data-k="example">Here's a proposal we won.</mark>{" "}
              <mark data-k="task">Draft one for Acme.</mark>{" "}
              <mark data-k="ask">Ask me 3 questions first.</mark>
            </p>
            <p className="bf-pa-key">
              <span data-k="context">Context</span>
              <span data-k="example">Example</span>
              <span data-k="task">Task</span>
              <span data-k="ask">Questions</span>
            </p>
          </div>
          <h3 className="bf-tile-title">Prompts that work</h3>
          <p className="bf-tile-body">A handful of your own, tested live and saved for next week.</p>
        </li>

        <li className="bf-tile bf-tile-project bf-tile-monet bf-tile-seine">
          <div className="bf-tile-visual bf-folder" aria-hidden="true">
            <span className="bf-folder-name">Project: My job</span>
            <span className="bf-folder-files">
              <span>brand-voice.pdf</span>
              <span>Q3-report.xlsx</span>
              <span>How I like things</span>
            </span>
          </div>
          <h3 className="bf-tile-title">A Project built around your job</h3>
          <p className="bf-tile-body">Your files and instructions loaded in, so every chat starts already briefed.</p>
        </li>

        <li className="bf-tile bf-tile-skill bf-tile-navy">
          <div className="bf-tile-visual bf-schedule" aria-hidden="true">
            <SkillSwitch />
            <span className="bf-schedule-when">
              <span className="bf-schedule-day">Every Monday</span>
              <span className="bf-schedule-time">8:00 AM</span>
            </span>
          </div>
          <h3 className="bf-tile-title">Your first skill on a schedule</h3>
          <p className="bf-tile-body">One weekly task that now runs without you starting it.</p>
        </li>
      </ul>
      <div className="bf-center bf-cta-row">
        <ReserveButton />
      </div>
    </section>
  );
}

/* ── 4. The map ───────────────────────────────────────────────────────────── */
const MAP: { hub: string; leaves: string[] }[] = [
  { hub: "Know you", leaves: ["CLAUDE.md", "Your brand file", "Your voice", "Projects"] },
  { hub: "Prompting", leaves: ["Context", "Examples", "Follow-up questions"] },
  { hub: "Connectors", leaves: ["Gmail", "Google Drive", "Calendar", "Web search"] },
  { hub: "Finished work", leaves: ["Documents", "Slides", "Charts"] },
  { hub: "Skills", leaves: ["Saved skills", "Scheduled tasks"] },
];

const W = 1200;
const H = 640;
const CX = W / 2;
const CY = H / 2;

function mapLayout() {
  return MAP.map((g, i) => {
    const a = -Math.PI / 2 + (i / MAP.length) * Math.PI * 2 + Math.PI / MAP.length;
    const hx = CX + Math.cos(a) * 300;
    const hy = CY + Math.sin(a) * 190;
    const leaves = g.leaves.map((l, j) => {
      const spread = 0.62;
      const la = a + (j - (g.leaves.length - 1) / 2) * (spread / Math.max(1, g.leaves.length - 1)) * 1.4;
      return { label: l, x: CX + Math.cos(la) * 470, y: CY + Math.sin(la) * 270 };
    });
    return { hub: g.hub, a, hx, hy, leaves };
  });
}

function CapabilityMap() {
  const [ref, inView] = useInView<HTMLElement>(0.25);
  const layout = mapLayout();
  return (
    <section ref={ref} className="bf-section bf-map" data-in={inView} aria-labelledby="bf-map-h">
      <h2 id="bf-map-h" className="bf-h2 bf-center">
        Everything we cover, on one map
      </h2>
      <p className="bf-lede bf-center">If it isn't on this map, you don't need it yet.</p>

      <svg className="bf-map-svg" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        {layout.map((g, i) => (
          <g key={g.hub} style={{ transitionDelay: `${200 + i * 120}ms` }} className="bf-map-group">
            <line x1={CX} y1={CY} x2={g.hx} y2={g.hy} className="bf-map-line bf-map-line-hub" pathLength={1} />
            {g.leaves.map((l) => (
              <line key={l.label} x1={g.hx} y1={g.hy} x2={l.x} y2={l.y} className="bf-map-line" pathLength={1} />
            ))}
            <text x={g.hx} y={g.hy} className="bf-map-hub" textAnchor="middle" dominantBaseline="middle">
              {g.hub}
            </text>
            {g.leaves.map((l) => (
              <text
                key={l.label}
                x={l.x}
                y={l.y}
                className="bf-map-leaf"
                textAnchor={l.x < CX - 40 ? "end" : l.x > CX + 40 ? "start" : "middle"}
                dominantBaseline="middle"
              >
                {l.label}
              </text>
            ))}
          </g>
        ))}
        <rect x={CX - 92} y={CY - 30} width={184} height={60} className="bf-map-core" />
        <text x={CX} y={CY} className="bf-map-core-text" textAnchor="middle" dominantBaseline="middle">
          Your Claude
        </text>
      </svg>

      {/* Phones and screen readers get the same map as a plain list. */}
      <ul className="bf-map-list">
        {MAP.map((g) => (
          <li key={g.hub}>
            <span className="bf-map-list-hub">{g.hub}</span>
            <span className="bf-map-list-leaves">{g.leaves.join(" · ")}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── 5. Who it's for ──────────────────────────────────────────────────────── */
/* Who it's for, in Isaac's persona-card template (the same carousel the other
   workshop pages run). Each card is the person at work on a bench beside a
   silver twin of themselves, thinking it through with them: the "thinking
   partner" idea. Walls are the Vivid tone-on-tone pairs; each title takes its
   wall's partner colour. */
const BENCH = "/images/persona-cards/beginners-bench";
const WHO_PERSONAS: Persona[] = [
  { key: "founders", title: "Founders", bg: "#E0B6A5", fg: "#9B0032", img: `${BENCH}/founders.webp`,
    alt: "A founder sketching a plan on a bench, a silver sculpture of himself beside him thinking it through",
    build: "Get the first draft of everything off your plate: proposals, hiring posts, investor updates, and the research behind them." },
  { key: "marketing", title: "Marketers", bg: "#BDCFDE", fg: "#283750", img: `${BENCH}/marketing.webp`,
    alt: "A marketer sorting campaign prints on a bench, a silver sculpture of himself beside him thinking it through",
    build: "Briefs, campaigns, and reports in your brand's voice, and answers pulled from your data without waiting on anyone." },
  { key: "sales", title: "Salespeople", bg: "#1D4F3F", fg: "#BACD2D", img: `${BENCH}/sales.webp`,
    alt: "A salesperson on a call with her laptop on a bench, a silver sculpture of herself beside her thinking it through",
    build: "Research the account before the call, write follow-ups that sound like you, and keep up with a pipeline that never slows down." },
  { key: "finance-ops", title: "Finance and\nops leads", bg: "#293B51", fg: "#B4C8E1", img: `${BENCH}/finance-ops.webp`,
    alt: "A finance lead marking up a chart on a bench, a silver sculpture of herself beside her thinking it through",
    build: "Make sense of messy spreadsheets, reconcile reports faster, and turn the numbers into a summary leadership will read." },
  { key: "designers", title: "Designers", bg: "#582E49", fg: "#C8AFF0", img: `${BENCH}/designers.webp`,
    alt: "A designer sketching on a bench, a silver sculpture of herself beside her thinking it through",
    build: "Briefs, research, and copy handled, so more of your week goes to the work itself." },
  { key: "consultants", title: "Consultants", bg: "#D4B0D9", fg: "#5E1756", img: `${BENCH}/consultants.webp`,
    alt: "A consultant reviewing slides on a tablet on a bench, a silver sculpture of himself beside him thinking it through",
    build: "Client prep, decks, and deliverables in half the time, in your own voice." },
];

function WhoFor() {
  return (
    <section className="bf-section bf-who-cards" aria-label="Who this workshop is for">
      <PersonaCarousel
        personas={WHO_PERSONAS}
        heading="Built for anyone who works at a computer"
        autoOpenFirstOnMobile={false}
        revealOnHover
        showPlus
      />
    </section>
  );
}

/* ── 6. The instructor ────────────────────────────────────────────────────── */

/* Your instructor, editorial portrait (Esther, 2026-11-02): a big portrait of
   Isaac on the bench with his silver twin, his name set large, one line on who
   he is. No logos, no stat callouts (Esther, 2026-11-02). Static. */
function Instructor() {
  const [ref, inView] = useInView<HTMLElement>();
  return (
    <section ref={ref} id="your-instructor" className="bf-section bf-instructor bf-inst-ed" data-in={inView} aria-labelledby="bf-inst-h">
      <div className="bf-inst">
        {/* Esther, 2026-11-02: Isaac laughing and looking up, in his patchwork
            jacket, on a softly faded, blurred Monet. The real laughing photo with
            only the clothes changed: the jacket was generated, then pasted back
            below the chin through a mask, so his face, laugh and cap are the
            original pixels (.gen-work/beginners/sweater/compj.py). */}
        <figure className="bf-inst-photo bf-inst-monet">
          <img
            src="/images/isaac-looking-jacket-1200.webp"
            srcSet="/images/isaac-looking-jacket-800.webp 800w, /images/isaac-looking-jacket-1200.webp 1200w, /images/isaac-looking-jacket.webp 1600w"
            sizes="(max-width: 1023px) 100vw, 50vw"
            alt="Isaac Rudansky laughing and looking up, in a backwards cap and a patchwork jacket"
            loading="lazy"
            decoding="async"
          />
        </figure>
        <div className="bf-inst-copy">
          <p className="bf-inst-kicker">Your instructor</p>
          <h2 id="bf-inst-h" className="bf-inst-name">
            Isaac Rudansky
          </h2>
          {/* From shared/instructors.ts (his bio on the other workshop pages); no
              Udemy name per Esther. */}
          <p className="bf-lede bf-inst-bio">
            Isaac founded AdVenture Media in 2012 and has been teaching ever since. More than 237,000 students have taken
            his best-selling Google Ads course, and PPC Hero named him one of the 25 most influential experts in the field.
          </p>
          <p className="bf-lede bf-inst-bio">
            Today he trains teams at Chase, ESPN, and T-Mobile to work with Claude. His style is to build in front of the
            room, show the draft that doesn't work, and fix it with everyone watching.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ── 7. Why it isn't free, and what your seat gets you ─────────────────────
   A dark band on a blurred painting: the one contrast moment between the
   white sections, for the argument that has to land. */
/* Esther, 2026-11-02: one line per item, check marks, no sub-lines. The
   attendee perks mirror the other workshop pages (attendees-receive.tsx);
   no Slack (still coming soon) and no refund line until the policy is
   confirmed. Heading per Esther; confirm the Anthropic-partner wording with
   Isaac against the June C&D before launch. */
const SEAT = [
  "3 hours live with bestselling instructor Isaac Rudansky",
  "Hands-on, in your own Claude account",
  "Live Q&A with Isaac",
  "Lifetime access to the recording",
  "2 weeks of personal email support",
  "Certificate signed by Isaac",
  "Your own Roger AI agent",
  "Lifetime access to our Claude Recipe Generator",
  "Lifetime access to OnPrompt Studio",
];

function Included() {
  const [ref, inView] = useInView<HTMLElement>();
  return (
    <section ref={ref} className="bf-section bf-dark" data-in={inView} aria-labelledby="bf-inc-h">
      <div className="bf-dark-art" aria-hidden="true" />
      <div className="bf-dark-inner">
        <div className="bf-dark-head">
          <h2 id="bf-inc-h" className="bf-h2">
            Learn from Anthropic's premier partners
          </h2>
          <div className="bf-cta-row">
            <ReserveButton />
          </div>
        </div>
        <div>
          <h3 className="bf-rows-label">Everything included in your workshop</h3>
          <ul className="bf-rows bf-rows-checks">
            {SEAT.map((t, i) => (
              <li key={t} style={{ transitionDelay: `${80 + i * 50}ms` }}>
                {/* The Vivid drawn tick, as on the other workshop pages' attendee list. */}
                <span className="bf-rows-check ar-tick" aria-hidden="true">
                  <DsIcon name="check" size={26} />
                </span>
                <span className="bf-rows-title">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ── 4b. Certificate + badge (Esther, 2026-11-02) ─────────────────────────────
   A short silent clip: the certificate hangs under a brass picture light on a
   Sunset studio wall, Isaac walks in, signs it, presses the silver badge on as
   a seal, and walks out. The certificate, his signature and the badge are the
   real artwork composited over every frame (not AI-drawn), so every word is
   sharp and the signature only appears under his pen. Source + finishing
   scripts: .gen-work/beginners/veo/ (isaac-cert-signing-v4.mp4, walk-16). The earlier
   still (Isaac's silver twin on a plinth) is images/beginners-cert/sculpt-sunset-*.
   The whole band is the clip's own wall colour and the clip bleeds off the right
   edge with its other edges feathered into it, so Isaac walks in from off the
   page. It plays once when it scrolls into view and rests on the signed
   certificate; reduced motion shows that last frame without playing. */
const CERT_REEL = {
  src: "/videos/beginners/certificate.mp4",
  poster: "/videos/beginners/certificate-poster.webp",
  label:
    "Isaac Rudansky signs a Claude for Beginners certificate of completion for Sarah Mitchell, framed under a picture light on an orange wall, then presses the silver badge onto it as a seal",
};

function Certificate() {
  const [ref, inView] = useInView<HTMLElement>(0.45);
  const video = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = video.current;
    if (!v || !inView) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const rest = () => (v.currentTime = Math.max(0, v.duration - 0.05));
      if (v.readyState >= 1) rest();
      else v.addEventListener("loadedmetadata", rest, { once: true });
      return;
    }
    v.play().catch(() => {});
  }, [inView]);
  return (
    <section ref={ref} className="bf-section bf-cert" data-in={inView} aria-labelledby="bf-cert-h">
      <div className="bf-cert-copy">
        <h2 id="bf-cert-h" className="bf-h2">
          Walk away with proof you did it
        </h2>
        <p className="bf-lede">
          Every attendee gets a certificate of completion signed by Isaac.
        </p>
        <div className="bf-cta-row">
          <ReserveButton />
        </div>
      </div>
      <figure className="bf-cert-art">
        <video
          ref={video}
          src={CERT_REEL.src}
          poster={CERT_REEL.poster}
          width={1600}
          height={900}
          muted
          playsInline
          preload="none"
          aria-label={CERT_REEL.label}
        />
      </figure>
    </section>
  );
}

export function BeginnersFlow({ reviews }: { reviews?: React.ReactNode }) {
  return (
    <div className="bf">
      <PromiseRow />
      <Agenda />
      <LeaveWith />
      {reviews && <div className="bf-reviews">{reviews}</div>}
      <WhoFor />
      <Instructor />
      <Certificate />
      <Included />
    </div>
  );
}
